/* eslint-disable no-nested-ternary */
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeEmailPhone, confirmChangeEmailPhone } from 'modules/session/actions';
import Input from 'components/UI/Input';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import useLiterals from 'utils/hooks/useLiterals';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import Box from 'components/UI/Box/Box';
import Button from 'components/UI/Button';
import { EMAIL_REGEX, OTP_LENGTH } from 'constants/global';
import Switch from 'components/UI/Switch';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import { STORAGE_EDIT_PROFILE } from 'constants/local';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from 'routes';
import InputOtp from 'components/UI/InputOtp';
import { sendSentryLog } from 'utils/sendSentryLog';

const STEPS = {
  FORM: 'form',
  OTP: 'otp',
};

const formatForm = (form, change, otps = false) => {
  return {
    email: change.email ? form.email || null : null,
    phone: change.phone ? (form.phone ? `+52${form.phone}` : null) : null,
    ...(otps && {
      otp_email: change.email ? form.otp_email : null,
      otp_phone: change.phone ? form.otp_phone : null,
    }),
  };
};

const EditProfile = () => {
  const literals = useLiterals('profile');
  const { user } = useSelector((state) => state.session);
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEPS.FORM);
  const [change, setChange] = useState({ email: false, phone: false });
  const [form, setForm] = useState({
    email: '',
    phone: '',
    otp_email: '',
    otp_phone: '',
  });
  const [error, setError] = useState(false);
  const [, setLastEdit] = useLocalStorage(STORAGE_EDIT_PROFILE, null, false);
  const navigate = useNavigate();

  const edited = useMemo(() => {
    return (
      (change.email || change.phone)
      && (!change.email || (form.email && EMAIL_REGEX.test(form.email) && form.email !== user.email))
      && (!change.phone || (form.phone && form.phone.length === 10 && form.phone !== user.phone))
    );
  }, [form, user, change]);

  const confirmed = useMemo(() => {
    return (
      (!change.email || (form.otp_email && form.otp_email.length === OTP_LENGTH))
      && (!change.phone || (form.otp_phone && form.otp_phone.length === OTP_LENGTH))
    );
  }, [form, change]);

  const handleRequestOtp = () => {
    dispatch(changeEmailPhone(formatForm(form, change)))
      .then(() => {
        setStep(STEPS.OTP);
      }).catch((e) => {
        const errorCode = e?.data?.error || 'ERROR_UPDATING_CLIENT';
        setError(literals[errorCode] || errorCode);
      });
  };

  const handleVerifyOtp = () => {
    dispatch(confirmChangeEmailPhone(formatForm(form, change, true)))
      .then(() => {
        setLastEdit(new Date().getTime());
        navigate(ROUTE_PATH.PROFILE);
      }).catch((e) => {
        sendSentryLog({
          error: e,
          data: {
            e: form?.email || '',
            p: form?.phone || '',
          },
        }, 'Edit profile: error verifying OTP');
        const errorCode = e?.data?.error || 'ERROR_UPDATING_CLIENT';
        setError(literals[errorCode] || errorCode);
      });
  };

  return (
    <Layout
      title={literals.editProfile}
      bottomMenu
      onBack
    >
      <AppShell variant='narrow'>
        {step === STEPS.FORM && (
          <Box>
            <p>{literals.editProfileInfo}</p>
            <div className='mb-6'>
              <Input
                label={literals.currentEmail}
                className='mb-4'
                value={user.email}
                disabled
              />
              <Switch
                className='mb-2'
                label={literals.changeEmail}
                checked={change.email}
                onChange={(e) => setChange({ ...change, email: e.target.checked })}
              />
              {change.email && (
                <Input
                  type='email'
                  label={literals.newEmail}
                  className='mb-2'
                  value={form.email}
                  onChange={(value) => setForm({ ...form, email: value })}
                />
              )}
            </div>
            <div>
              <Input
                type='tel'
                label={literals.currentPhone}
                className='mb-4'
                value={user.phone.replace('+52', '')}
                disabled
              />
              <Switch
                className='mb-2'
                label={literals.changePhone}
                checked={change.phone}
                onChange={(e) => setChange({ ...change, phone: e.target.checked })}
              />
              {change.phone && (
                <Input
                  type='tel'
                  label={literals.newPhone}
                  className='mb-2'
                  value={form.phone}
                  onChange={(value) => setForm({ ...form, phone: value })}
                />
              )}
              <Button
                className='mt-6'
                text={literals.common.continue}
                onClick={handleRequestOtp}
                disabled={!edited}
              />
            </div>
          </Box>
        )}
        {step === STEPS.OTP && (
          <Box>
            <div className='mb-12'>
              <p className='mb-6'>{change.email && change.phone ? literals.confirmInfoBoth : (change.email ? literals.confirmInfoEmail : literals.confirmInfoPhone)}</p>
              {change.email && (
                <>
                  <p className='font-semibold mb-4'>{literals.emailOTP}</p>
                  <InputOtp
                    className='mb-6'
                    onChange={(v) => setForm({ ...form, otp_email: v })}
                  />
                </>
              )}
              {change.phone && (
                <>
                  <p className='font-semibold mb-4'>{literals.phoneOTP}</p>
                  <InputOtp onChange={(v) => setForm({ ...form, otp_phone: v })} />
                </>
              )}
            </div>
            <Button
              className='mb-6'
              type='secondary'
              text={literals.common.cancel}
              onClick={() => setStep(STEPS.FORM)}
            />
            <Button
              text={literals.common.confirm}
              onClick={handleVerifyOtp}
              disabled={!confirmed}
            />
          </Box>
        )}
        {error && (
          <ErrorModal
            description={error}
            onClose={() => setError(false)}
          />
        )}
      </AppShell>
    </Layout>
  );
};

export default EditProfile;
